import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useChart } from '../contexts/ChartContext';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const SIGN_COLORS: Record<string, string> = {
  Aries: '#ef5350', Taurus: '#66bb6a', Gemini: '#ffa726', Cancer: '#42a5f5',
  Leo: '#ff7043', Virgo: '#26a69a', Libra: '#ab47bc', Scorpio: '#8d6e63',
  Sagittarius: '#5c6bc0', Capricorn: '#78909c', Aquarius: '#26c6da', Pisces: '#ec407a',
};

const BirthTimeRectification: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentChart } = useChart();

  const [birthDetails, setBirthDetails] = useState<any>(null);
  const [rtfRange, setRtfRange] = useState<number>(30);
  const [rtfStep, setRtfStep] = useState<number>(2);
  const [rtfData, setRtfData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Birth Time Rectification - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';
    // Load birth details for the header
    const chartId = id === 'preview' ? currentChart?._id ?? id : id;
    if (chartId && chartId !== 'preview') {
      axios.get(`${API_BASE_URL}/charts/${chartId}`)
        .then(r => setBirthDetails(r.data.data.chart?.birthDetails))
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [id]);

  const fetchRectification = async () => {
    const chartId = id === 'preview' ? currentChart?._id ?? id : id;
    if (!chartId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API_BASE_URL}/charts/${chartId}/rectification`, {
        params: { range: rtfRange, step: rtfStep },
      });
      setRtfData(resp.data.data);
    } catch (e: any) {
      setError(e.response?.data?.message ?? 'Rectification calculation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: '#fafafa', minHeight: '100vh' }}>

      {/* Navigation buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
        {id && id !== 'preview' && (
          <>
            <Button component={Link} to={`/chart/${id}`} variant="outlined" color="secondary" size="small">
              Nadi Analysis
            </Button>
            <Button component={Link} to={`/chart/${id}/kp`} variant="outlined" color="secondary" size="small">
              KP Analysis
            </Button>
            <Button component={Link} to={`/chart/${id}/divisional`} variant="outlined" color="secondary" size="small">
              Vedic Analysis
            </Button>
            <Button component={Link} to={`/chart/${id}/rectification`} variant="contained" color="primary" size="small">
              Rectification
            </Button>
          </>
        )}
      </Stack>

      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center', bgcolor: 'primary.main', p: 3, borderRadius: 2 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
          {birthDetails?.name ?? 'Birth Time Rectification'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
          Analyse how Ascendant &amp; Navamsa Lagna shift with birth time adjustments
        </Typography>
      </Box>

      {/* Intro */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Use this tool to narrow down the correct birth time when it is uncertain. The chart recalculates
        at every step and shows which minute marks a change of Lagna or D9 sign.
      </Typography>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems={{ sm: 'flex-end' }} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Time Range (±minutes)
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={rtfRange}
              onChange={(_, v) => v !== null && setRtfRange(v)}
              size="small"
            >
              {[15, 30, 60, 90].map(v => (
                <ToggleButton key={v} value={v}>{v} min</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Step Size
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={rtfStep}
              onChange={(_, v) => v !== null && setRtfStep(v)}
              size="small"
            >
              {[1, 2, 5, 10].map(v => (
                <ToggleButton key={v} value={v}>{v} min</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={fetchRectification}
            disabled={loading}
            sx={{ minWidth: 160 }}
          >
            {loading ? 'Calculating…' : 'Calculate'}
          </Button>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {rtfData && (() => {
        const { candidates, originalTime } = rtfData;

        const withChanges = candidates.map((c: any, i: number) => ({
          ...c,
          lagnaChanged: i > 0 && candidates[i - 1].ascendant.signIndex !== c.ascendant.signIndex,
          d9Changed:    i > 0 && candidates[i - 1].d9Ascendant.signIndex !== c.d9Ascendant.signIndex,
        }));

        return (
          <>
            {/* Timeline bars */}
            <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Ascendant Sign Timeline
              </Typography>
              <Box sx={{ display: 'flex', height: 44, borderRadius: 1, overflow: 'hidden', border: '1px solid #ddd' }}>
                {candidates.map((c: any) => {
                  const col = SIGN_COLORS[c.ascendant.signName] ?? '#90a4ae';
                  const isOrig = c.offset === 0;
                  return (
                    <Tooltip
                      key={c.offset}
                      title={`${c.time} (${c.offset >= 0 ? '+' : ''}${c.offset} min): ${c.ascendant.label}`}
                    >
                      <Box sx={{
                        flex: 1,
                        bgcolor: col,
                        opacity: isOrig ? 1 : 0.65,
                        borderLeft: isOrig ? '3px solid #000' : 'none',
                        borderRight: isOrig ? '3px solid #000' : 'none',
                        cursor: 'default',
                        transition: 'opacity .15s',
                        '&:hover': { opacity: 1 },
                      }} />
                    </Tooltip>
                  );
                })}
              </Box>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                D9 (Navamsa) Lagna Timeline
              </Typography>
              <Box sx={{ display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden', border: '1px solid #ddd' }}>
                {candidates.map((c: any) => {
                  const col = SIGN_COLORS[c.d9Ascendant.signName] ?? '#90a4ae';
                  const isOrig = c.offset === 0;
                  return (
                    <Tooltip
                      key={c.offset}
                      title={`${c.time}: D9 ${c.d9Ascendant.signName}`}
                    >
                      <Box sx={{
                        flex: 1,
                        bgcolor: col,
                        opacity: isOrig ? 1 : 0.65,
                        borderLeft: isOrig ? '3px solid #000' : 'none',
                        borderRight: isOrig ? '3px solid #000' : 'none',
                        cursor: 'default',
                        transition: 'opacity .15s',
                        '&:hover': { opacity: 1 },
                      }} />
                    </Tooltip>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">-{rtfRange} min</Typography>
                <Typography variant="caption" color="text.secondary">
                  Original: <strong>{originalTime}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">+{rtfRange} min</Typography>
              </Box>

              {/* Sign legend */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(new Set(candidates.map((c: any) => c.ascendant.signName))).map((sign: any) => (
                  <Chip
                    key={sign}
                    label={sign}
                    size="small"
                    sx={{ bgcolor: SIGN_COLORS[sign] ?? '#90a4ae', color: '#fff', fontWeight: 600, fontSize: 11 }}
                  />
                ))}
              </Box>
            </Paper>

            {/* Detail table */}
            <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: '#1a237e' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'white' }}>
                  Candidate Times — Detail Table
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Offset</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Lagna (Sign)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Lagna Degree</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>D9 Lagna</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Moon Nakshatra</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withChanges.map((c: any) => {
                      const isOrig  = c.offset === 0;
                      const signCol = SIGN_COLORS[c.ascendant.signName] ?? '#90a4ae';
                      const d9Col   = SIGN_COLORS[c.d9Ascendant.signName] ?? '#90a4ae';
                      return (
                        <TableRow
                          key={c.offset}
                          sx={{
                            bgcolor: isOrig ? '#e8eaf6' : c.lagnaChanged ? '#fff9c4' : 'inherit',
                            borderTop: c.lagnaChanged ? '2px solid #f57f17' : undefined,
                          }}
                        >
                          <TableCell sx={{ fontWeight: isOrig ? 700 : 400, color: isOrig ? 'primary.main' : 'inherit' }}>
                            {isOrig ? '0 ★' : `${c.offset >= 0 ? '+' : ''}${c.offset}`}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: isOrig ? 700 : 400 }}>
                            {c.time}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={c.ascendant.signName}
                              size="small"
                              sx={{ bgcolor: signCol, color: '#fff', fontWeight: 600, fontSize: 11 }}
                            />
                            {c.lagnaChanged && (
                              <Chip label="Sign ↑" size="small" color="warning" sx={{ ml: 0.5, fontSize: 10 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {c.ascendant.degree}°{String(c.ascendant.minute).padStart(2, '0')}'
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={c.d9Ascendant.signName}
                              size="small"
                              sx={{ bgcolor: d9Col, color: '#fff', fontWeight: 600, fontSize: 11 }}
                            />
                            {c.d9Changed && (
                              <Chip label="D9 ↑" size="small" color="info" sx={{ ml: 0.5, fontSize: 10 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{c.moonNakshatra ?? '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #eee' }}>
                <Typography variant="caption" color="text.secondary">
                  ★ = recorded birth time · Yellow rows = Lagna sign change · Orange border = D9 change.
                  Compare the Lagna that best fits known life events and physical appearance.
                </Typography>
              </Box>
            </Paper>
          </>
        );
      })()}

      {!rtfData && !loading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Ready to Rectify
          </Typography>
          <Typography color="text.secondary">
            Select a range and step, then click <strong>Calculate</strong>.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default BirthTimeRectification;
